import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';

// Auth Context
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Components
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Dashboard from './components/Dashboard';
import ScheduleRequest from './components/ScheduleRequest';
import ScheduleViolations from './components/ScheduleViolations';
import AdminScheduleViolations from './components/AdminScheduleViolations';
import PayrollAnalytics from './components/PayrollAnalytics';
import UserManagement from './components/UserManagement';
import AvailabilityView from './components/AvailabilityView';
import ScheduleGeneration from './components/ScheduleGeneration';
import ScheduleDisplay from './components/ScheduleDisplay';
import ProfileManagement from './components/ProfileManagement';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import PublicBeachSchedule from './components/PublicBeachSchedule';
import SurfConditionsDashboard from './components/SurfConditionsDashboard';
import ErrorBoundary from './components/ErrorBoundary';
import { safeLocalStorage } from './utils/safeFunctions';
import SummerInReview from './components/SummerInReview';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  return user ? children : <Navigate to="/login" />;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { user } = useAuth();
  const [userPermissions, setUserPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetch('/api/user/permissions', {
        headers: {
          'Authorization': `Bearer ${safeLocalStorage.getItem('token')}`
        }
      })
      .then(response => response.json())
      .then(data => {
        // The API returns permissions directly as an array
        if (Array.isArray(data)) {
          setUserPermissions(data);
        }
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching permissions:', error);
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [user]);

  if (loading) {
    return <div className="loading-spinner"></div>;
  }

  // Check for specific admin permissions based on the route
  const hasAdminAccess = userPermissions.some(permission => 
    ['generate_schedule', 'view_users', 'manage_beaches', 'manage_vehicles', 'manage_ranks', 'manage_permissions', 'view_payroll_analytics'].includes(permission)
  );
  return hasAdminAccess ? children : <Navigate to="/dashboard" />;
};

// Main App Content Component
const AppContent = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [shouldShowReview, setShouldShowReview] = useState(false);
  const [reviewChecked, setReviewChecked] = useState(false);

  useEffect(() => {
    const checkReview = async () => {
      try {
        if (!user) {
          setReviewChecked(true);
          return;
        }
        // Ask server whether to show review
        const res = await fetch('/api/summer-review/should-show', {
          headers: {
            'Authorization': `Bearer ${safeLocalStorage.getItem('token')}`
          }
        });
        const data = await res.json();
        setShouldShowReview(Boolean(data?.show));
      } catch (e) {
        setShouldShowReview(false);
      } finally {
        setReviewChecked(true);
      }
    };
    checkReview();
  }, [user]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // When review is required, force route to /summer-review and hide navigation/footer
  if (user && reviewChecked && shouldShowReview && location.pathname !== '/summer-review') {
    return <Navigate to="/summer-review" />;
  }

  return (
    <div className="App">
      {user && location.pathname !== '/public/schedule' && location.pathname !== '/summer-review' && <Navigation />}
      <main className="main-content">
        <Routes>
          {/* Public Routes */}
          <Route 
            path="/public/schedule" 
            element={<PublicBeachSchedule />} 
          />
          <Route 
            path="/public/surf-conditions" 
            element={<SurfConditionsDashboard />} 
          />
          
          {/* Authenticated Routes */}
          <Route 
            path="/login" 
            element={user ? <Navigate to="/dashboard" /> : <Login />} 
          />
          <Route 
            path="/register" 
            element={user ? <Navigate to="/dashboard" /> : <Register />} 
          />
          <Route 
            path="/forgot-password" 
            element={user ? <Navigate to="/dashboard" /> : <ForgotPassword />} 
          />
          <Route 
            path="/reset-password" 
            element={user ? <Navigate to="/dashboard" /> : <ResetPassword />} 
          />
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/summer-review"
            element={
              <ProtectedRoute>
                <SummerInReview />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/schedule-request" 
            element={
              <ProtectedRoute>
                <ScheduleRequest />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/schedule-violations" 
            element={
              <ProtectedRoute>
                <ScheduleViolations />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/availability" 
            element={
              <ProtectedRoute>
                <AvailabilityView />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/schedule-display" 
            element={
              <ProtectedRoute>
                <ScheduleDisplay />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/profile" 
            element={
              <ProtectedRoute>
                <ProfileManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/schedule-generation" 
            element={
              <AdminRoute>
                <ScheduleGeneration />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/schedule-violations" 
            element={
              <AdminRoute>
                <AdminScheduleViolations />
              </AdminRoute>
            } 
          />
          <Route 
            path="/admin/payroll-analytics" 
            element={
              <AdminRoute>
                <PayrollAnalytics />
              </AdminRoute>
            } 
          />
          <Route 
            path="/user-management" 
            element={
              <AdminRoute>
                <UserManagement />
              </AdminRoute>
            } 
          />
          <Route 
            path="/" 
            element={user ? <Navigate to="/dashboard" /> : <Navigate to="/login" />} 
          />
        </Routes>
      </main>
      {location.pathname !== '/public/schedule' && location.pathname !== '/summer-review' && <Footer />}
    </div>
  );
};

function App() {
  const [isAppVisible, setIsAppVisible] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    // PWA lifecycle management
    const handleVisibilityChange = () => {
      setIsAppVisible(!document.hidden);
      
      // When app becomes visible after being hidden, check if we need to refresh
      if (!document.hidden) {
        // Small delay to ensure the app is fully restored
        setTimeout(() => {
          // Check if the app content is properly loaded
          const rootElement = document.getElementById('root');
          if (rootElement && (!rootElement.innerHTML || rootElement.innerHTML.trim() === '')) {
            console.log('App content missing after restoration, reloading...');
            window.location.reload();
          }
        }, 100);
      }
    };

    const handleOnlineStatus = () => {
      setIsOnline(navigator.onLine);
      
      // When coming back online, refresh if the app seems broken
      if (navigator.onLine) {
        setTimeout(() => {
          const rootElement = document.getElementById('root');
          if (rootElement && (!rootElement.innerHTML || rootElement.innerHTML.trim() === '')) {
            console.log('App content missing after coming online, reloading...');
            window.location.reload();
          }
        }, 500);
      }
    };

    const handlePageShow = (event) => {
      // Handle page restoration from bfcache (back/forward cache)
      if (event.persisted) {
        console.log('Page restored from bfcache');
        // Check if app content is intact
        setTimeout(() => {
          const rootElement = document.getElementById('root');
          if (rootElement && (!rootElement.innerHTML || rootElement.innerHTML.trim() === '')) {
            console.log('App content missing after bfcache restoration, reloading...');
            window.location.reload();
          }
        }, 100);
      }
    };

    const handleFocus = () => {
      // App regained focus - check integrity
      setTimeout(() => {
        const rootElement = document.getElementById('root');
        if (rootElement && (!rootElement.innerHTML || rootElement.innerHTML.trim() === '')) {
          console.log('App content missing after focus, reloading...');
          window.location.reload();
        }
      }, 100);
    };

    // Service Worker message handling
    const handleSWMessage = (event) => {
      if (event.data && event.data.type === 'CACHE_REFRESHED') {
        console.log('Service worker cache refreshed');
        // Optionally reload the page to get fresh content
        // window.location.reload();
      }
    };

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('focus', handleFocus);
    
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleSWMessage);
    }

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('focus', handleFocus);
      
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleSWMessage);
      }
    };
  }, []);

  // Show loading state when app is not visible or offline
  if (!isAppVisible && !isOnline) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Restoring app...</p>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
